import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useAllOrganisationsAPI from '../../RolesAndPermissions/CreateNewRole/hooks/useAllOrganisationsAPI';
import { Member } from '../../../apollo/queries';
import { Organisation } from '../context/RolesContext';

interface Props {
  isOrgMember: boolean;
  user: Member | null;
  fetchRolesSelection(code: string): void;
  setSelectedOrgs(orgs: string[]): void;
}

const useOrganisationTabs = ({
  isOrgMember,
  user,
  fetchRolesSelection,
  setSelectedOrgs,
}: Props) => {
  const params = useParams<{ code: string }>();
  const { organisations: orgs } = useAllOrganisationsAPI(isOrgMember);
  const getAllOrgs = () => {
    return orgs.filter((o) => !orgTabs.find((ot) => ot.code === o.code));
  };

  const [orgTabs, setTabs] = useState<Organisation[]>([]);
  const organisations = isOrgMember
    ? [{ name: orgTabs[0]?.code, code: orgTabs[0]?.code }]
    : getAllOrgs();

  const setOrgTabs = (tabs: Organisation[]) => {
    setTabs(tabs);
    setSelectedOrgs(tabs.map((t) => t.code));
  };

  const updateOrganisationForRow = (code: string, index: number) => {
    const updatedOrg = orgTabs.map((org, i) => ({
      ...org,
      code: index === i ? code : org.code,
    }));
    setOrgTabs(updatedOrg);
    fetchRolesSelection(code);
  };

  const addOrganisationRow = () => {
    if (organisations.length > 0) {
      const [org] = [...organisations];
      const newRows = [...orgTabs, { code: org.code }];
      setOrgTabs(newRows);
      fetchRolesSelection(org.code);
    } else {
      const newRows = [...orgTabs, { code: '' }];
      setOrgTabs(newRows);
    }
  };

  const removeOrganisationRow = (index: number) =>
    setOrgTabs(orgTabs.filter((_, i) => i !== index));

  useEffect(() => {
    if (isOrgMember) {
      setOrgTabs([{ code: params.code }]);
      fetchRolesSelection(params.code);
    }
  }, []);

  useEffect(() => {
    if (!isOrgMember) {
      if (user && user.membershipsV2) {
        const tabs: Organisation[] = user.membershipsV2.map(
          (mem): Organisation => {
            fetchRolesSelection(mem.organisation);
            return {
              code: mem.organisation,
            };
          },
        );

        setOrgTabs(tabs);
      }
    }
  }, [user]);

  return {
    organisations,
    orgTabs,
    updateOrganisationForRow,
    addOrganisationRow,
    removeOrganisationRow,
  };
};

export default useOrganisationTabs;
